<template>
  <div class="networks-page">
    <WeCircularNavbar />

    <section class="policy-page">
      <div class="policy-container">
        <h1 class="page-title">
          Networks
        </h1>

        <p class="page-intro">
          Connect with key stakeholders, build strategic partnerships, and unlock new collaboration opportunities across
          the ecosystem.
        </p>

        <div class="filters">
          <div class="search-bar">
            <input
              v-model.trim="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search networks or contacts..."
            />
          </div>

          <select v-model="selectedCountry" class="filter-select">
            <option value="">Select Country</option>
            <option v-for="country in availableCountries" :key="country" :value="country">
              {{ country }}
            </option>
          </select>
        </div>

        <section class="cards-list">
          <article v-for="(item, index) in paginatedNetworks" :key="'network-' + index" class="policy-card">
            <h3 class="card-title">
              {{ item.name }}
            </h3>

            <div class="card-meta">
              <div class="meta-item">
                <span class="meta-label">Country</span>
                <span class="meta-value">{{ item.countryRegion }}</span>
              </div>

              <div class="meta-item">
                <span class="meta-label">Mission</span>
                <span class="meta-value">{{ item.mission }}</span>
              </div>

              <div class="meta-item meta-item--right">
                <span class="meta-label">Website</span>
                <a :href="normalizeLink(item.website)" target="_blank" rel="noopener noreferrer" class="meta-link">
                  {{ websiteLabel(item.website) }}
                </a>
              </div>
            </div>

            <a
              v-if="item.website"
              :href="normalizeLink(item.website)"
              target="_blank"
              rel="noopener noreferrer"
              class="learn-more"
            >
              Learn More →
            </a>
          </article>
        </section>

        <div v-if="totalPages > 1" class="pagination-dots">
          <button
            v-for="page in totalPages"
            :key="'page-' + page"
            type="button"
            class="pagination-dot"
            :class="{ 'pagination-dot--active': currentPage === page }"
            @click="currentPage = page"
          ></button>
        </div>
      </div>
    </section>

    <section class="page-section contacts-section">
      <div class="page-container">
        <h2 class="page-title">Contacts</h2>

        <p class="page-intro">
          Do not hesitate to get in touch with the organisations listed below.
        </p>

        <div class="contacts-grid">
          <article v-for="(contact, index) in visibleContacts" :key="'contact-' + index" class="contact-card">
            <div class="contact-card__image-wrapper">
              <img :src="contact.image || defaultContactImage" :alt="contact.name" class="contact-card__image" />
            </div>

            <div class="contact-card__body">
              <div class="contact-card__title-row">
                <span class="contact-card__flag" :class="flagClass(contact.countryRegion)"></span>

                <h3 class="contact-card__title">
                  {{ contact.name }}
                </h3>
              </div>

              <div class="contact-card__info">
                <p v-if="contact.address"><strong>Address:</strong> {{ contact.address }}</p>

                <p>
                  <strong>e-mail:</strong>
                  <span>{{ contact.email || '' }}</span>
                </p>

                <p v-if="contact.website">
                  <strong>Website:</strong>
                  <a :href="normalizeLink(contact.website)" target="_blank" rel="noopener noreferrer">
                    {{ websiteLabel(contact.website) }}
                  </a>
                </p>
              </div>

              <a
                v-if="contact.link || contact.website"
                :href="normalizeLink(contact.link || contact.website)"
                target="_blank"
                rel="noopener noreferrer"
                class="contact-card__learn-more"
              >
                Learn More
                <span class="contact-card__arrow">→</span>
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'
import defaultContactImage from '../../assets/img/we-circular/1-1@4x.png'
import { networksData } from './networksData.js'
import { contactsData } from './contactsData.js'

export default {
  name: 'NetworksPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      defaultContactImage,
      searchQuery: '',
      selectedCountry: '',
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    searchTerm() {
      return String(this.searchQuery || '')
        .toLowerCase()
        .trim()
    },

    allNetworks() {
      if (!networksData || typeof networksData !== 'object') {
        return []
      }

      return Object.values(networksData).flat()
    },

    allContacts() {
      if (!contactsData || typeof contactsData !== 'object') {
        return []
      }

      return Object.values(contactsData).flat()
    },

    availableCountries() {
      const networkCountries = this.allNetworks.map(item => item.countryRegion).filter(Boolean)
      const contactCountries = this.allContacts.map(item => item.countryRegion).filter(Boolean)

      return [...new Set([...networkCountries, ...contactCountries])].sort((a, b) => a.localeCompare(b))
    },

    visibleNetworks() {
      return this.allNetworks.filter(item => {
        const matchesSearch =
          !this.searchTerm ||
          [item.name, item.countryRegion, item.mission, item.website].some(value =>
            String(value || '')
              .toLowerCase()
              .includes(this.searchTerm)
          )

        const matchesCountry = !this.selectedCountry || item.countryRegion === this.selectedCountry

        return matchesSearch && matchesCountry
      })
    },

    paginatedNetworks() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      return this.visibleNetworks.slice(start, start + this.itemsPerPage)
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.visibleNetworks.length / this.itemsPerPage))
    },

    visibleContacts() {
      return this.allContacts.filter(contact => {
        const matchesSearch =
          !this.searchTerm ||
          [contact.name, contact.countryRegion, contact.address, contact.email, contact.website, contact.link].some(
            value =>
              String(value || '')
                .toLowerCase()
                .includes(this.searchTerm)
          )

        const matchesCountry = !this.selectedCountry || contact.countryRegion === this.selectedCountry

        return matchesSearch && matchesCountry
      })
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    selectedCountry() {
      this.currentPage = 1
    }
  },
  methods: {
    normalizeLink(link) {
      if (!link) return '#'
      if (link.startsWith('http://') || link.startsWith('https://')) return link
      return `https://${link}`
    },
    websiteLabel(link) {
      if (!link) return ''
      return link.replace(/^https?:\/\//, '').replace(/\/$/, '')
    },
    flagClass(country) {
      const normalized = String(country || '')
        .toLowerCase()
        .replaceAll('_', '-')
        .replaceAll(' ', '-')

      const map = {
        austria: 'flag-austria',
        romania: 'flag-romania',
        germany: 'flag-germany',
        croatia: 'flag-croatia',
        bulgaria: 'flag-bulgaria',
        slovenia: 'flag-slovenia',
        czechia: 'flag-czechia',
        'czech-republic': 'flag-czechia',
        slovakia: 'flag-slovakia',
        moldova: 'flag-moldova',
        hungary: 'flag-hungary',
        serbia: 'flag-serbia',
        'bosnia-and-herzegovina': 'flag-bosnia'
      }

      return map[normalized] || 'flag-default'
    }
  }
}
</script>

<style scoped>
.networks-page {
  min-height: 100vh;
  background: #f3f3f3;
}

.policy-page {
  min-height: 100vh;
  background: #f3f3f3;
  padding: 22px 0 64px;
}

.policy-container {
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  padding: 0 38px;
  box-sizing: border-box;
}

.back-button {
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: flex-start !important;
  height: 30px;
  min-width: 66px;
  padding: 0 12px;
  margin-bottom: 22px;
  border: 1px solid #003399;
  border-radius: 8px;
  background: transparent;
  color: #003399;
  line-height: 1;
  cursor: pointer;
  margin-left: 0;
}

.page-title {
  margin: 0;
  max-width: 760px;
  color: #003399;
  line-height: 1.06;
  font-weight: 600;
  letter-spacing: -0.03em;
  text-align: left;
}

.page-intro {
  max-width: 30%;
  margin: 14px 0 34px;
  color: #0e6eb6;
  line-height: 1.5;
  text-align: left;
}

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1.3fr) minmax(180px, 1fr);
  gap: 14px;
  margin: 0 0 34px;
}

.search-bar {
  width: 100%;
}

.search-input {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid #cfd9e8;
  border-radius: 10px;
  background: #ffffff;
  color: #003399;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: #6c86a8;
}

.search-input:focus {
  border-color: #0e6eb6;
  box-shadow: 0 0 0 3px rgba(14, 110, 182, 0.12);
}

.filter-select {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 1px solid #cfd9e8;
  border-radius: 10px;
  background: #ffffff;
  color: #003399;
  font-size: 16px;
  outline: none;
  box-sizing: border-box;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.policy-card {
  background: #f7f9fc;
  border: 1px solid #cfd9e8;
  border-radius: 8px;
  padding: 12px 14px;
  color: #003399;
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0 0 10px;
  font-size: 20px;
  line-height: 1.3;
  font-weight: 700;
  color: #003399;
  text-align: left;
}

.card-meta {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  align-items: start;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.meta-item--right {
  text-align: left;
}

.meta-label {
  font-size: 16px;
  font-weight: 700;
  color: #0e6eb6;
}

.meta-value,
.meta-link {
  font-size: 16px;
  color: #6c86a8;
  text-decoration: none;
  line-height: 1.3;
  word-break: break-word;
}

.meta-link:hover {
  text-decoration: underline;
}

.learn-more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  font-size: 16px;
  color: #18baa8;
  font-weight: 600;
  justify-content: flex-start;
  align-self: flex-start;
  text-decoration: none;
}

.learn-more:hover {
  text-decoration: underline;
}

.pagination-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 22px;
}

.pagination-dot {
  width: 10px;
  height: 10px;
  border: none;
  border-radius: 50%;
  background: #cbd2df;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.pagination-dot--active {
  background: #18baa8;
  transform: scale(1.1);
}

.page-section {
  padding: 56px 0;
}

.page-container {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.contacts-section {
  padding-top: 34px;
  padding-bottom: 80px;
}

.section-title {
  margin: 0 0 14px;
  color: #0d5b59;
  font-size: 48px;
  font-weight: 700;
  line-height: 1.08;
}

.section-intro {
  max-width: 760px;
  margin: 0 0 34px;
  color: #61706d;
  font-size: 16px;
  line-height: 1.7;
}

.contacts-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;
}

.contact-card {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 16px;
  border: 1.5px solid #0d5b59;
  border-radius: 28px;
  background: #f8f8f5;
}

.contact-card__image-wrapper {
  width: 100%;
  aspect-ratio: 1 / 1;
  margin-bottom: 18px;
  border-radius: 20px;
  overflow: hidden;
  background: #d7e1de;
}

.contact-card__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.contact-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.contact-card__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
}

.contact-card__flag {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid #d7d7d7;
  overflow: hidden;
  background: #d9d9d9;
}

.flag-hungary {
  background: linear-gradient(to bottom, #ce2939 0 33.33%, #ffffff 33.33% 66.66%, #477a1e 66.66% 100%);
}

.flag-austria {
  background: linear-gradient(to bottom, #ed2939 0 33.33%, #ffffff 33.33% 66.66%, #ed2939 66.66% 100%);
}

.flag-romania {
  background: linear-gradient(to right, #002b7f 0 33.33%, #fcd116 33.33% 66.66%, #ce1126 66.66% 100%);
}

.flag-germany {
  background: linear-gradient(to bottom, #000000 0 33.33%, #dd0000 33.33% 66.66%, #ffce00 66.66% 100%);
}

.flag-croatia {
  background: linear-gradient(to bottom, #ff0000 0 33.33%, #ffffff 33.33% 66.66%, #171796 66.66% 100%);
}

.flag-bulgaria {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #00966e 33.33% 66.66%, #d62612 66.66% 100%);
}

.flag-slovenia {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #005ce6 33.33% 66.66%, #d50000 66.66% 100%);
}

.flag-czechia,
.flag-czech-republic {
  background: linear-gradient(135deg, #11457e 0 35%, #ffffff 35% 67%, #d7141a 67% 100%);
}

.flag-slovakia {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #0b4ea2 33.33% 66.66%, #ee1c25 66.66% 100%);
}

.flag-serbia {
  background: linear-gradient(to bottom, #c6363c 0 33.33%, #0c4076 33.33% 66.66%, #ffffff 66.66% 100%);
}

.flag-moldova {
  background: linear-gradient(to right, #003f87 0 33.33%, #ffd200 33.33% 66.66%, #ce1126 66.66% 100%);
}

.flag-bosnia {
  background: linear-gradient(135deg, #002f6c 0 60%, #fcd116 60% 100%);
}

.flag-default {
  background: #d9d9d9;
}

.contact-card__title {
  margin: 0;
  color: #31354b;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.contact-card__info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 26px;
}

.contact-card__info p {
  margin: 0;
  color: #2a2f38;
  font-size: 15px;
  line-height: 1.55;
  word-break: break-word;
}

.contact-card__info a {
  color: #2a2f38;
  text-decoration: none;
}

.contact-card__info a:hover {
  text-decoration: underline;
}

.contact-card__learn-more {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #202437;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
}

.contact-card__learn-more:hover {
  opacity: 0.85;
}

.contact-card__arrow {
  font-size: 18px;
  line-height: 1;
}

@media (max-width: 1100px) {
  .policy-container {
    max-width: 92%;
    padding: 0 20px;
  }

  .page-intro {
    max-width: 48%;
  }

  .contacts-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .page-title {
    font-size: 28px;
  }

  .page-intro {
    max-width: 100%;
  }

  .filters {
    grid-template-columns: 1fr;
  }

  .card-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .section-title {
    font-size: 36px;
  }

  .contacts-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 520px) {
  .policy-page {
    padding-bottom: 48px;
  }

  .page-container {
    width: min(100% - 28px, 1200px);
  }

  .page-title {
    font-size: 22px;
  }

  .page-intro {
    font-size: 12px;
  }

  .section-title {
    font-size: 30px;
  }

  .contacts-grid {
    grid-template-columns: 1fr;
  }

  .policy-card {
    padding: 14px;
  }

  .card-title {
    font-size: 12px;
  }

  .meta-label,
  .meta-value,
  .meta-link,
  .learn-more {
    font-size: 11px;
  }

  .contact-card {
    border-radius: 24px;
  }

  .contact-card__image-wrapper {
    border-radius: 18px;
  }

  .search-input,
  .filter-select {
    height: 42px;
    font-size: 14px;
  }
}
</style>
